import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Video, VideoOff, Mic, MicOff, Monitor, MonitorOff, Phone, PhoneOff,
  Users, Copy, Maximize, Minimize, Circle, Square, Clock, Link,
  AlertTriangle, X, Check, Loader
} from 'lucide-react';

const NAVY = '#0B2A4A';
const TEAL = '#1F8CA0';
const RED = '#B8323A';
const BG = '#F6F8FA';
const SURFACE = '#FFFFFF';

function TelehealthPage({ user }) {
  // State: Rooms & Patients
  const [patients, setPatients] = useState([]);
  const [activeRooms, setActiveRooms] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [roomName, setRoomName] = useState('');
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [creatingRoom, setCreatingRoom] = useState(false);

  // State: Active Call
  const [currentRoom, setCurrentRoom] = useState(null);
  const [inCall, setInCall] = useState(false);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [showParticipants, setShowParticipants] = useState(false);

  // State: Media controls
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [videoEnabled, setVideoEnabled] = useState(true);
  const [screenSharing, setScreenSharing] = useState(false);
  const [recording, setRecording] = useState(false);

  // State: Recording consent
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [waitingForConsent, setWaitingForConsent] = useState(false);
  const [consentGranted, setConsentGranted] = useState(false);
  const [recordingActive, setRecordingActive] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);

  // State: Call timing
  const [callDuration, setCallDuration] = useState(0);

  // State: Connection
  const [connectionStatus, setConnectionStatus] = useState('idle'); // idle, connecting, connected, error
  const [connectionQuality, setConnectionQuality] = useState('good'); // good, fair, poor

  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnectionRef = useRef(null);
  const socketRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const callTimerRef = useRef(null);
  const recordingTimerRef = useRef(null);

  // ── Load patients on mount ────────────────────────────────────────────────
  useEffect(() => {
    const loadPatients = async () => {
      try {
        const result = await window.zignagenixAPI?.invoke?.('patient:list', {}) || [];
        setPatients(Array.isArray(result) ? result : []);
      } catch (err) {
        console.error('Failed to load patients:', err);
      }
    };
    loadPatients();
  }, []);

  // ── Socket.io setup ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!socketRef.current) {
      const token = window.zignagenixAPI?.getSessionToken?.();
      const { io } = window;
      if (io && token) {
        const socket = io(window.location.origin, {
          auth: { token },
          transports: ['websocket', 'polling'],
        });
        socketRef.current = socket;

        // Video signaling
        socket.on('video:offer', handleRemoteOffer);
        socket.on('video:answer', handleRemoteAnswer);
        socket.on('video:ice', handleRemoteICE);
        socket.on('video:participant-joined', handleParticipantJoined);
        socket.on('video:participant-left', handleParticipantLeft);
        socket.on('video:consent-request', handleConsentRequest);
        socket.on('video:consent-response', handleConsentResponse);
        socket.on('video:toggle-media', handleRemoteMediaToggle);
        socket.on('video:recording-started', () => setRecordingActive(true));
        socket.on('video:recording-stopped', () => setRecordingActive(false));
      }
    }

    return () => {
      // Socket remains open for page lifecycle
    };
  }, []);

  // ── Load active rooms periodically ─────────────────────────────────────────
  useEffect(() => {
    const loadRooms = async () => {
      try {
        const rooms = await window.zignagenixAPI?.invoke?.('video:active-rooms', {}) || [];
        setActiveRooms(Array.isArray(rooms) ? rooms : []);
      } catch (err) {
        console.error('Failed to load rooms:', err);
      }
    };
    loadRooms();
    const interval = setInterval(loadRooms, 5000);
    return () => clearInterval(interval);
  }, []);

  // ── Create video room ────────────────────────────────────────────────────────
  const handleCreateRoom = async () => {
    if (!selectedPatient || !roomName.trim()) return;

    setCreatingRoom(true);
    try {
      const room = await window.zignagenixAPI?.invoke?.('video:create-room', {
        patientId: selectedPatient.id,
        roomName: roomName.trim(),
        clinicianId: user?.id,
        clinicianName: user?.name || 'Clinician',
      });

      if (room) {
        setCurrentRoom(room);
        setShowCreateRoom(false);
        setSelectedPatient(null);
        setRoomName('');
        // Auto-join
        await joinRoom(room);
      }
    } catch (err) {
      console.error('Failed to create room:', err);
    } finally {
      setCreatingRoom(false);
    }
  };

  // ── Join existing room ───────────────────────────────────────────────────────
  const joinRoom = async (room) => {
    setConnectionStatus('connecting');
    try {
      // Get local media
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
      });

      setLocalStream(stream);
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }

      // Setup peer connection
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
        ],
      });

      peerConnectionRef.current = pc;

      // Add local stream tracks
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      // Handle remote stream
      pc.ontrack = (event) => {
        if (event.streams && event.streams[0]) {
          setRemoteStream(event.streams[0]);
          if (remoteVideoRef.current) {
            remoteVideoRef.current.srcObject = event.streams[0];
          }
        }
      };

      // Handle ICE candidates
      pc.onicecandidate = (event) => {
        if (event.candidate && socketRef.current) {
          socketRef.current.emit('video:ice', {
            roomId: room.roomId,
            candidate: event.candidate,
          });
        }
      };

      // Create and send offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      socketRef.current?.emit('video:join-room', {
        roomId: room.roomId,
        offer: offer,
      });

      setInCall(true);
      setConnectionStatus('connected');
      setConsentGranted(false);
      setRecordingActive(false);

      // Start call timer
      setCallDuration(0);
      if (callTimerRef.current) clearInterval(callTimerRef.current);
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);

      // Monitor connection quality
      monitorConnectionQuality(pc);
    } catch (err) {
      console.error('Failed to join room:', err);
      setConnectionStatus('error');
    }
  };

  // ── One-click join hand-off from My Day → Calendar ─────────────────────────
  // The calendar's "Join" button creates the room, stashes it in sessionStorage
  // and navigates here. Pick it up once on mount, drop straight into the call,
  // and clear the hand-off so a refresh doesn't rejoin a stale room.
  const pendingJoinRef = useRef(false);
  useEffect(() => {
    if (pendingJoinRef.current) return;
    let room = null;
    try {
      const raw = sessionStorage.getItem('pendingVideoRoom');
      if (raw) room = JSON.parse(raw);
    } catch { room = null; }
    if (room && room.roomId) {
      pendingJoinRef.current = true;
      try { sessionStorage.removeItem('pendingVideoRoom'); } catch { /* private mode */ }
      setCurrentRoom(room);
      joinRoom(room);
    }
  }, []);

  // ── Monitor connection quality ───────────────────────────────────────────────
  const monitorConnectionQuality = (pc) => {
    const check = async () => {
      try {
        const stats = await pc.getStats();
        let packetsLost = 0;
        let packetsSent = 0;
        stats.forEach(report => {
          if (report.type === 'inbound-rtp' && report.mediaType === 'video') {
            packetsLost += report.packetsLost || 0;
          }
          if (report.type === 'outbound-rtp' && report.mediaType === 'video') {
            packetsSent += report.packetsSent || 0;
          }
        });
        const lossRatio = packetsSent > 0 ? packetsLost / packetsSent : 0;
        if (lossRatio > 0.05) setConnectionQuality('poor');
        else if (lossRatio > 0.02) setConnectionQuality('fair');
        else setConnectionQuality('good');
      } catch (err) {
        console.error('Quality check failed:', err);
      }
    };
    check();
    const interval = setInterval(check, 5000);
    return () => clearInterval(interval);
  };

  // ── Handle remote offer ──────────────────────────────────────────────────────
  const handleRemoteOffer = async (data) => {
    try {
      const pc = peerConnectionRef.current;
      if (!pc) return;

      await pc.setRemoteDescription(new RTCSessionDescription(data.offer));

      const answer = await pc.createAnswer();
      await pc.setLocalDescription(answer);

      socketRef.current?.emit('video:answer', {
        roomId: currentRoom?.roomId,
        answer: answer,
      });
    } catch (err) {
      console.error('Error handling remote offer:', err);
    }
  };

  // ── Handle remote answer ─────────────────────────────────────────────────────
  const handleRemoteAnswer = async (data) => {
    try {
      const pc = peerConnectionRef.current;
      if (pc && pc.remoteDescription === null) {
        await pc.setRemoteDescription(new RTCSessionDescription(data.answer));
      }
    } catch (err) {
      console.error('Error handling remote answer:', err);
    }
  };

  // ── Handle ICE candidate ─────────────────────────────────────────────────────
  const handleRemoteICE = async (data) => {
    try {
      const pc = peerConnectionRef.current;
      if (pc && data.candidate) {
        await pc.addIceCandidate(new RTCIceCandidate(data.candidate));
      }
    } catch (err) {
      console.error('Error adding ICE candidate:', err);
    }
  };

  // ── Participant events ───────────────────────────────────────────────────────
  const handleParticipantJoined = (data) => {
    setParticipants(prev => [...prev, data]);
  };

  const handleParticipantLeft = (data) => {
    setParticipants(prev => prev.filter(p => p.participantId !== data.participantId));
  };

  const handleRemoteMediaToggle = (data) => {
    setParticipants(prev =>
      prev.map(p =>
        p.participantId === data.participantId
          ? { ...p, audioEnabled: data.audioEnabled, videoEnabled: data.videoEnabled }
          : p
      )
    );
  };

  // ── Recording consent ────────────────────────────────────────────────────────
  const handleConsentRequest = (data) => {
    setShowConsentModal(true);
  };

  const handleConsentResponse = (data) => {
    setWaitingForConsent(false);
    if (data.consent) {
      setConsentGranted(true);
      startRecording();
    }
  };

  // ── Request recording consent ────────────────────────────────────────────────
  const handleRequestConsent = async () => {
    setShowConsentModal(false);
    setWaitingForConsent(true);
    socketRef.current?.emit('video:consent-request', {
      roomId: currentRoom?.roomId,
      callId: currentRoom?.callId,
    });
    // Timeout after 30s
    setTimeout(() => {
      if (waitingForConsent) {
        setWaitingForConsent(false);
      }
    }, 30000);
  };

  // ── Start recording ──────────────────────────────────────────────────────────
  const startRecording = () => {
    if (!remoteStream) {
      console.warn('Remote stream not available');
      return;
    }

    const audioContext = new AudioContext();
    const audioSource = audioContext.createMediaStreamAudioSource(remoteStream);
    const dest = audioContext.createMediaStreamDestination();
    audioSource.connect(dest);

    const canvas = document.createElement('canvas');
    canvas.width = 1280;
    canvas.height = 720;
    const ctx = canvas.getContext('2d');

    const combinedStream = new MediaStream();
    dest.stream.getAudioTracks().forEach(track => combinedStream.addTrack(track));
    const videoTrack = canvas.captureStream(30).getVideoTracks()[0];
    if (videoTrack) combinedStream.addTrack(videoTrack);

    const mimeType = 'video/webm;codecs=vp9,opus';
    const mediaRecorder = new MediaRecorder(combinedStream, { mimeType });

    recordingChunksRef.current = [];
    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        recordingChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setRecording(true);
    setRecordingDuration(0);

    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    recordingTimerRef.current = setInterval(() => {
      setRecordingDuration(prev => prev + 1);
    }, 1000);

    socketRef.current?.emit('video:recording-started', {
      roomId: currentRoom?.roomId,
      callId: currentRoom?.callId,
    });
  };

  // ── Stop recording ───────────────────────────────────────────────────────────
  const stopRecording = async () => {
    if (!mediaRecorderRef.current) return;

    return new Promise((resolve) => {
      mediaRecorderRef.current.onstop = async () => {
        const blob = new Blob(recordingChunksRef.current, { type: 'video/webm' });
        recordingChunksRef.current = [];

        // Convert to base64 and upload
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result.split(',')[1];
          try {
            await window.zignagenixAPI?.invoke?.('video:upload-recording', {
              recordingId: `rec-${Date.now()}`,
              fileData: base64,
              duration: recordingDuration,
              callId: currentRoom?.callId,
              roomId: currentRoom?.roomId,
            });
          } catch (err) {
            console.error('Failed to upload recording:', err);
          }
          resolve();
        };
        reader.readAsDataURL(blob);
      };

      mediaRecorderRef.current.stop();
      setRecording(false);
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);

      socketRef.current?.emit('video:recording-stopped', {
        roomId: currentRoom?.roomId,
        callId: currentRoom?.callId,
      });
    });
  };

  // ── Toggle audio ─────────────────────────────────────────────────────────────
  const toggleAudio = () => {
    if (localStream) {
      const enabled = !audioEnabled;
      localStream.getAudioTracks().forEach(track => {
        track.enabled = enabled;
      });
      setAudioEnabled(enabled);
      socketRef.current?.emit('video:toggle-media', {
        roomId: currentRoom?.roomId,
        audioEnabled: enabled,
        videoEnabled,
      });
    }
  };

  // ── Toggle video ─────────────────────────────────────────────────────────────
  const toggleVideo = () => {
    if (localStream) {
      const enabled = !videoEnabled;
      localStream.getVideoTracks().forEach(track => {
        track.enabled = enabled;
      });
      setVideoEnabled(enabled);
      socketRef.current?.emit('video:toggle-media', {
        roomId: currentRoom?.roomId,
        audioEnabled,
        videoEnabled: enabled,
      });
    }
  };

  // ── Copy patient link ────────────────────────────────────────────────────────
  const copyPatientLink = () => {
    if (currentRoom?.patientJoinUrl) {
      navigator.clipboard.writeText(currentRoom.patientJoinUrl);
      // Show toast
    }
  };

  // ── End call ─────────────────────────────────────────────────────────────────
  const endCall = async () => {
    if (recording) {
      await stopRecording();
    }

    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }

    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    if (callTimerRef.current) clearInterval(callTimerRef.current);
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);

    socketRef.current?.emit('video:leave-room', {
      roomId: currentRoom?.roomId,
    });

    try {
      await window.zignagenixAPI?.invoke?.('video:end-room', {
        roomId: currentRoom?.roomId,
      });
    } catch (err) {
      console.error('Failed to end room:', err);
    }

    setInCall(false);
    setCurrentRoom(null);
    setRemoteStream(null);
    setParticipants([]);
    setCallDuration(0);
    setConnectionStatus('idle');
  };

  // ── Format duration ──────────────────────────────────────────────────────────
  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  // ── Render: Create Room Modal ────────────────────────────────────────────────
  if (showCreateRoom && !inCall) {
    return (
      <div style={{ padding: '20px', maxWidth: '500px', margin: '0 auto' }}>
        <h2 style={{ color: NAVY, marginBottom: '20px' }}>Start Telehealth Session</h2>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', color: NAVY, fontWeight: 500 }}>
            Select Patient
          </label>
          <select
            value={selectedPatient?.id || ''}
            onChange={(e) => {
              const p = patients.find(x => x.id === parseInt(e.target.value));
              setSelectedPatient(p);
            }}
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid #ddd`,
              borderRadius: '4px',
              fontFamily: 'inherit',
            }}
          >
            <option value="">-- Select a patient --</option>
            {patients.map(p => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', marginBottom: '5px', color: NAVY, fontWeight: 500 }}>
            Session Name
          </label>
          <input
            type="text"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            placeholder="e.g., Follow-up Visit"
            style={{
              width: '100%',
              padding: '10px',
              border: `1px solid #ddd`,
              borderRadius: '4px',
              fontFamily: 'inherit',
            }}
          />
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleCreateRoom}
            disabled={!selectedPatient || !roomName.trim() || creatingRoom}
            style={{
              flex: 1,
              padding: '10px',
              background: creatingRoom ? '#ccc' : TEAL,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: creatingRoom ? 'default' : 'pointer',
              fontWeight: 500,
            }}
          >
            {creatingRoom ? <Loader size={16} style={{ display: 'inline', marginRight: '5px' }} /> : null}
            {creatingRoom ? 'Creating...' : 'Start Session'}
          </button>
          <button
            onClick={() => setShowCreateRoom(false)}
            style={{
              flex: 1,
              padding: '10px',
              background: '#f0f0f0',
              color: NAVY,
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Render: In-call view ─────────────────────────────────────────────────────
  if (inCall && currentRoom) {
    const qualityColor = connectionQuality === 'good' ? '#4CAF50' : connectionQuality === 'fair' ? '#FF9800' : '#F44336';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: NAVY }}>
        {/* Header */}
        <div
          style={{
            padding: '15px 20px',
            background: NAVY,
            color: 'white',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: `1px solid rgba(255,255,255,0.1)`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Video size={20} />
            <span style={{ fontSize: '18px', fontWeight: 600 }}>Telehealth</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <button
              onClick={copyPatientLink}
              style={{
                padding: '8px 12px',
                background: TEAL,
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                fontSize: '14px',
              }}
            >
              <Link size={16} />
              Copy Patient Link
            </button>
            <div
              style={{
                width: '12px',
                height: '12px',
                borderRadius: '50%',
                background: qualityColor,
              }}
            />
            <span style={{ fontSize: '12px', opacity: 0.8 }}>
              {connectionQuality}
            </span>
          </div>
        </div>

        {/* Video Area */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            background: '#000',
          }}
        >
          {/* Remote video (large) */}
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              background: '#000',
            }}
          />

          {/* Local video (PiP) */}
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            style={{
              position: 'absolute',
              bottom: '20px',
              right: '20px',
              width: '200px',
              height: '150px',
              borderRadius: '8px',
              background: '#000',
              border: `2px solid ${SURFACE}`,
              objectFit: 'cover',
            }}
          />

          {/* Recording indicator */}
          {recordingActive && (
            <div
              style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                background: RED,
                color: 'white',
                padding: '8px 12px',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              <Circle size={8} style={{ fill: 'white' }} />
              REC {formatDuration(recordingDuration)}
            </div>
          )}

          {/* Call timer */}
          <div
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              background: 'rgba(0,0,0,0.6)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
            }}
          >
            <Clock size={16} />
            {formatDuration(callDuration)}
          </div>

          {/* Participants button */}
          <button
            onClick={() => setShowParticipants(!showParticipants)}
            style={{
              position: 'absolute',
              bottom: '20px',
              left: '20px',
              background: TEAL,
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <Users size={16} />
            Participants ({participants.length + 1})
          </button>

          {/* Participants overlay */}
          {showParticipants && (
            <div
              style={{
                position: 'absolute',
                bottom: '70px',
                left: '20px',
                background: SURFACE,
                borderRadius: '8px',
                padding: '15px',
                minWidth: '250px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}
            >
              <h4 style={{ color: NAVY, marginBottom: '10px' }}>Participants</h4>
              <div style={{ fontSize: '14px', color: '#555', marginBottom: '8px' }}>
                <div>You (Clinician)</div>
              </div>
              {participants.map(p => (
                <div key={p.participantId} style={{ fontSize: '14px', color: '#555', marginBottom: '8px' }}>
                  <div>{p.name}</div>
                  <div style={{ fontSize: '12px', opacity: 0.7 }}>
                    {p.audioEnabled ? <Mic size={12} /> : <MicOff size={12} />}
                    {p.videoEnabled ? <Video size={12} /> : <VideoOff size={12} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Control Bar */}
        <div
          style={{
            padding: '20px',
            background: NAVY,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '15px',
            borderTop: `1px solid rgba(255,255,255,0.1)`,
          }}
        >
          {/* Mute Audio */}
          <button
            onClick={toggleAudio}
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: audioEnabled ? TEAL : RED,
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={audioEnabled ? 'Mute' : 'Unmute'}
          >
            {audioEnabled ? <Mic size={24} /> : <MicOff size={24} />}
          </button>

          {/* Mute Video */}
          <button
            onClick={toggleVideo}
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: videoEnabled ? TEAL : RED,
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={videoEnabled ? 'Stop Video' : 'Start Video'}
          >
            {videoEnabled ? <Video size={24} /> : <VideoOff size={24} />}
          </button>

          {/* Record */}
          <button
            onClick={handleRequestConsent}
            disabled={recording || consentGranted}
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: recording || consentGranted ? RED : '#555',
              color: 'white',
              border: 'none',
              cursor: recording || consentGranted ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="Request Recording Consent"
          >
            {recording ? <Square size={24} /> : <Circle size={24} />}
          </button>

          {/* End Call */}
          <button
            onClick={endCall}
            style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              background: RED,
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title="End Call"
          >
            <PhoneOff size={24} />
          </button>
        </div>

        {/* Consent Request Modal */}
        {showConsentModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: SURFACE,
                borderRadius: '8px',
                padding: '30px',
                maxWidth: '400px',
                textAlign: 'center',
              }}
            >
              <h3 style={{ color: NAVY, marginBottom: '15px' }}>Request Recording Consent</h3>
              <p style={{ color: '#555', marginBottom: '20px' }}>
                Do you want to request recording consent from the patient?
              </p>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleRequestConsent}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: TEAL,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 500,
                  }}
                >
                  Request Consent
                </button>
                <button
                  onClick={() => setShowConsentModal(false)}
                  style={{
                    flex: 1,
                    padding: '10px',
                    background: '#f0f0f0',
                    color: NAVY,
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Waiting for Consent Modal */}
        {waitingForConsent && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: SURFACE,
                borderRadius: '8px',
                padding: '30px',
                maxWidth: '400px',
                textAlign: 'center',
              }}
            >
              <div style={{ marginBottom: '20px' }}>
                <Loader size={48} style={{ margin: '0 auto', animation: 'spin 2s linear infinite' }} />
              </div>
              <h3 style={{ color: NAVY, marginBottom: '10px' }}>Waiting for Consent...</h3>
              <p style={{ color: '#555' }}>Patient is reviewing your recording request.</p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── Render: Lobby (list of rooms) ────────────────────────────────────────────
  return (
    <div style={{ display: 'flex', height: '100vh', background: BG }}>
      {/* Sidebar */}
      <div
        style={{
          width: '300px',
          background: SURFACE,
          borderRight: `1px solid #e0e0e0`,
          padding: '20px',
          overflowY: 'auto',
        }}
      >
        <h2 style={{ color: NAVY, marginBottom: '20px', fontSize: '18px' }}>
          Room Management
        </h2>

        <button
          onClick={() => setShowCreateRoom(true)}
          style={{
            width: '100%',
            padding: '12px',
            background: TEAL,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 500,
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
        >
          <Video size={18} />
          Start Telehealth Session
        </button>

        <h3 style={{ color: NAVY, fontSize: '14px', fontWeight: 600, marginBottom: '15px' }}>
          Active Rooms ({activeRooms.length})
        </h3>

        {activeRooms.length === 0 ? (
          <p style={{ color: '#999', fontSize: '14px' }}>No active rooms</p>
        ) : (
          activeRooms.map(room => (
            <div
              key={room.roomId}
              onClick={() => {
                setCurrentRoom(room);
                joinRoom(room);
              }}
              style={{
                padding: '12px',
                background: '#f5f5f5',
                borderRadius: '4px',
                marginBottom: '10px',
                cursor: 'pointer',
                border: `1px solid #e0e0e0`,
              }}
            >
              <div style={{ fontWeight: 500, color: NAVY, marginBottom: '4px' }}>
                {room.roomName}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                Patient: {room.patientName}
              </div>
              <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                Created by: {room.creatorName}
              </div>
              <div style={{ fontSize: '12px', color: '#999' }}>
                Duration: {formatDuration(room.durationSeconds || 0)}
              </div>
              <div
                style={{
                  marginTop: '8px',
                  display: 'inline-block',
                  padding: '4px 8px',
                  background: room.status === 'active' ? '#4CAF50' : '#FFC107',
                  color: 'white',
                  borderRadius: '3px',
                  fontSize: '12px',
                }}
              >
                {room.status}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Main Area */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '40px',
          textAlign: 'center',
        }}
      >
        <div style={{ marginBottom: '20px' }}>
          <Video size={64} color={TEAL} />
        </div>
        <h1 style={{ color: NAVY, marginBottom: '10px', fontSize: '28px' }}>
          Welcome to Telehealth
        </h1>
        <p style={{ color: '#666', marginBottom: '30px', fontSize: '16px' }}>
          Select a patient and start a new telehealth session, or join an existing room.
        </p>
        <button
          onClick={() => setShowCreateRoom(true)}
          style={{
            padding: '12px 24px',
            background: TEAL,
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 500,
            fontSize: '16px',
          }}
        >
          Start New Session
        </button>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default TelehealthPage;
